import {
  Breadcrumbs,
  Button,
  Grid,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  useDeleteCompanyMutation,
  useUpdateClientMutation,
} from '@bvi/admin-panel/entities/clients/api-slice';
import {
  IClientDetailsHeaderProperties,
  IClientStateButtonConfig,
} from '@bvi/admin-panel/feature/clients/ui/client-details-header/types';
import { EditCompanyForm } from '@bvi/admin-panel/feature/clients/ui/edit-company-form';
import { AdminPanelRoutesPaths } from '@bvi/admin-panel/shared/constants/routes';
import { ClientStatus } from '@bvi/api-interfaces/entity/client';
import { isServerError } from '@bvi/axios-query';
import { ConfirmActionButton, PaperModal } from '@bvi/common-components';
import { NotistackMessageVariants, useNotistack } from '@bvi/notistack';

const CLIENT_STATE_BUTTONS: Record<ClientStatus, IClientStateButtonConfig> = {
  [ClientStatus.Unconfirmed]: {
    nextState: ClientStatus.Active,
    caption: 'clients.details.buttons.confirm.caption',
    question: 'clients.details.buttons.confirm.question',
    props: undefined,
  },
  [ClientStatus.Active]: {
    nextState: ClientStatus.Archived,
    caption: 'clients.details.buttons.block.caption',
    question: 'clients.details.buttons.block.question',
    props: {
      color: 'error',
    },
  },
  [ClientStatus.Archived]: {
    nextState: ClientStatus.Active,
    caption: 'clients.details.buttons.restore.caption',
    question: 'clients.details.buttons.restore.question',
    props: undefined,
  },
};

export const ClientDetailsHeader: FC<IClientDetailsHeaderProperties> = ({
  client,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showNotificationMessage } = useNotistack();

  const [updateClient] = useUpdateClientMutation();
  const [deleteCompany, { isLoading: isDeletingCompany }] =
    useDeleteCompanyMutation();

  const [isOpen, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSetState = useCallback(
    (status: ClientStatus) => async () => {
      if (client) {
        await updateClient([client.id, { status }]);
      }
    },
    [updateClient, client],
  );

  const handleDeleteCompany = useCallback(async () => {
    if (!client) {
      return;
    }

    try {
      await deleteCompany(client.company.id).unwrap();
      navigate(AdminPanelRoutesPaths.CLIENTS_LIST);
    } catch (error) {
      if (isServerError(error)) {
        showNotificationMessage(
          { type: error.type },
          NotistackMessageVariants.ERROR,
        );
      }
    }
  }, [client, deleteCompany, navigate, showNotificationMessage]);

  return (
    <Grid container my={4} alignItems="center">
      <Grid item xs={12}>
        <Breadcrumbs separator="|">
          <Link underline="hover" href={AdminPanelRoutesPaths.CLIENTS_LIST}>
            {t('clients.details.breadcrumbs.clients')}
          </Link>
          {client ? (
            <Typography>{client?.company.name}</Typography>
          ) : (
            <Skeleton width="10em" />
          )}
        </Breadcrumbs>
      </Grid>
      <Grid item xs={7}>
        <Stack direction="row" alignItems="center" mt={2}>
          {client ? (
            <Typography variant="h1">{client?.company.name}</Typography>
          ) : (
            <Skeleton width="10em" sx={{ fontSize: '1.3rem' }} />
          )}
        </Stack>
      </Grid>
      <Grid item xs={5} justifyContent="flex-end" display="flex">
        <Button variant="outlined" disabled={!client} onClick={handleOpen}>
          {t('clients.details.buttons.editCompany.caption')}
        </Button>
        {client && (
          <>
            <PaperModal open={isOpen} onClose={handleClose}>
              <EditCompanyForm client={client} onSuccess={handleClose} />
            </PaperModal>
            <ConfirmActionButton
              variant="outlined"
              sx={{ ml: 2 }}
              question={t(CLIENT_STATE_BUTTONS[client.status].question)}
              confirm={t('dialog.confirm')}
              cancel={t('dialog.cancel')}
              onClick={handleSetState(
                CLIENT_STATE_BUTTONS[client.status].nextState,
              )}
              {...CLIENT_STATE_BUTTONS[client.status].props}
            >
              {t(CLIENT_STATE_BUTTONS[client.status].caption)}
            </ConfirmActionButton>
            <ConfirmActionButton
              variant="contained"
              color="error"
              confirmColor="error"
              sx={{ ml: 2 }}
              disabled={isDeletingCompany}
              question={t('clients.details.buttons.deleteCompany.question')}
              description={t(
                'clients.details.buttons.deleteCompany.description',
              )}
              confirm={t('clients.details.buttons.deleteCompany.confirm')}
              cancel={t('dialog.cancel')}
              onClick={handleDeleteCompany}
            >
              {t('clients.details.buttons.deleteCompany.caption')}
            </ConfirmActionButton>
          </>
        )}
      </Grid>
    </Grid>
  );
};
